import { useCallback, useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MoreSentinel } from "@/components/PacketList";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { fmt } from "@/lib/status";

const EMPTY = { start: "", end: "", actor: "", action: "", result: "", record: "" };
const dayIso = (d, plus = 0) => { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + plus); return x.toISOString(); };

function Pick({ value, onChange, items, all, id }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className="h-9 w-48" data-testid={`audit-filter-${id}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="__all">{all}</SelectItem>{items.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [v, setV] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [rec, setRec] = useState("");
  const [facets, setFacets] = useState({ actors: [], actions: [], results: [] });
  const seq = useRef(0);
  const params = { start: f.start ? dayIso(f.start) : "", end: f.end ? dayIso(f.end, 1) : "", actor: f.actor, action: f.action, result: f.result, record: f.record };
  const load = useCallback(async (c) => {
    const my = c ? seq.current : ++seq.current;
    try {
      const r = await api.get("/audit", { params: { ...params, cursor: c || 0 } });
      if (my !== seq.current) { return; }
      setRows((p) => (c ? [...p, ...r.data.items] : r.data.items));
      setCursor(r.data.next_cursor);
    } catch (e) { toast.error(errMsg(e)); }
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/audit/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setF((x) => ({ ...x, record: rec.trim() })), 400); return () => clearTimeout(t); }, [rec]);
  const set = (k) => (val) => setF({ ...f, [k]: val });
  const active = Object.values(f).some(Boolean);
  return (
    <div className="space-y-3" data-testid="audit-log">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={() => api.get("/audit/verify").then((r) => setV(r.data))} data-testid="audit-verify-btn">Verify hash chain</Button>
        {v && <span className={`flex items-center gap-1 text-sm font-semibold ${v.ok ? "text-green-700" : "text-red-700"}`} data-testid="audit-verify-result">
          {v.ok ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}{v.ok ? `Intact - ${v.checked} events verified` : `Broken at event #${v.broken_at_seq}`}</span>}
      </div>
      <div className="flex flex-wrap items-end gap-2 bg-white border rounded-xl p-3" data-testid="audit-filters">
        <label className="text-xs text-slate-500">From<Input type="date" value={f.start} max={f.end || undefined} onChange={(e) => set("start")(e.target.value)} className="h-9 w-40" data-testid="audit-filter-start" /></label>
        <label className="text-xs text-slate-500">To<Input type="date" value={f.end} min={f.start || undefined} onChange={(e) => set("end")(e.target.value)} className="h-9 w-40" data-testid="audit-filter-end" /></label>
        <Pick id="actor" value={f.actor} onChange={set("actor")} items={facets.actors} all="Everyone" />
        <Pick id="action" value={f.action} onChange={set("action")} items={facets.actions} all="All actions" />
        <Pick id="result" value={f.result} onChange={set("result")} items={facets.results} all="Any result" />
        <Input value={rec} onChange={(e) => setRec(e.target.value)} placeholder="Record / packet / request ID" className="h-9 w-56" data-testid="audit-filter-record" />
        {active && <Button size="sm" variant="ghost" onClick={() => { setF(EMPTY); setRec(""); }} data-testid="audit-filter-clear">Clear</Button>}
      </div>
      <p className="text-xs text-slate-500">Append-only, hash-chained per organization. No edit or delete API exists. Details contain no patient demographics. Newest first; older events load as you scroll.</p>
      <Table className="bg-white border rounded-xl"><TableHeader><TableRow><TableHead>#</TableHead><TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Record</TableHead><TableHead>Result</TableHead><TableHead>Hash</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((e) => (
          <TableRow key={e.seq} data-testid={`audit-row-${e.seq}`}><TableCell>{e.seq}</TableCell><TableCell className="text-xs whitespace-nowrap">{fmt(e.at)}</TableCell><TableCell className="text-xs">{e.actor_name} <span className="text-slate-400">{e.role}</span></TableCell>
            <TableCell className="font-mono text-xs">{e.action}</TableCell><TableCell className="font-mono text-[10px] text-slate-500">{e.packet_id || e.entity_id || ""}</TableCell>
            <TableCell className={e.result === "success" ? "" : "text-red-700"}>{e.result}</TableCell><TableCell className="font-mono text-[10px] text-slate-500">{e.hash.slice(0, 12)}</TableCell></TableRow>))}
          {!rows.length && <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6" data-testid="audit-empty">No events match these filters.</TableCell></TableRow>}
        </TableBody></Table>
      {cursor && <ul><MoreSentinel onMore={() => load(cursor)} /></ul>}
    </div>
  );
}
